/**
 * Admin 我的好友（主号直推）— shared table + optional Modal wrapper.
 * Same口径 as frontend MyFriends: users.referrer_id + master_id IS NULL.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, Modal, Space, Table, Typography } from 'antd';
import { api } from '../api/client';

export type DirectFriendRow = {
  user_id: number;
  username: string;
  nickname: string | null;
  player_number: string | null;
  level_code: string;
  star_count: number;
  left_sum: string;
  right_sum: string;
  masked_username: string;
};

export type DirectFriendsData = {
  user_id: number;
  username: string;
  nickname: string | null;
  player_number: string | null;
  total: number;
  rows: DirectFriendRow[];
};

function fmtSum(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

type PanelProps = {
  userId?: number;
  playerNumber?: string;
  account?: string;
  enabled?: boolean;
  onOpenPlayer?: (playerNumber: string, userId: number) => void;
};

export function DirectFriendsPanel({
  userId,
  playerNumber,
  account,
  enabled = true,
  onOpenPlayer,
}: PanelProps) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const friendsQuery = useQuery({
    queryKey: ['admin-binary-tree-my-friends', userId ?? null, playerNumber ?? null, account ?? null, query],
    queryFn: () =>
      api.get<DirectFriendsData>('/api/admin/binary-tree/my-friends', {
        ...(userId != null ? { user_id: userId } : {}),
        ...(playerNumber ? { player_number: playerNumber } : {}),
        ...(account ? { account } : {}),
        ...(query ? { search: query } : {}),
      }),
    enabled: enabled && (userId != null || !!playerNumber || !!account),
    staleTime: 30_000,
  });

  const data = friendsQuery.data;
  const rows = data?.rows ?? [];

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          allowClear
          placeholder="筛选直推编号 / 账号 / 昵称"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => setQuery(search.trim())}
          style={{ width: 220 }}
        />
        <Button onClick={() => setQuery(search.trim())}>搜索</Button>
        {query ? (
          <Button
            type="link"
            onClick={() => {
              setSearch('');
              setQuery('');
            }}
          >
            清除
          </Button>
        ) : null}
        <Typography.Text type="secondary">
          共 {data?.total ?? '—'} 位主号直推（不含子账户）
        </Typography.Text>
      </Space>

      <Table<DirectFriendRow>
        size="small"
        rowKey={(r) => r.user_id}
        loading={friendsQuery.isLoading || friendsQuery.isFetching}
        dataSource={rows}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        columns={[
          {
            title: '玩家编号',
            width: 120,
            render: (_: unknown, row) => {
              const num = row.player_number ?? String(row.user_id);
              if (!onOpenPlayer) return <>{'★'.repeat(row.star_count)}{num}</>;
              return (
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onOpenPlayer(num, row.user_id)}>
                  {'★'.repeat(row.star_count)}
                  {num}
                </Button>
              );
            },
          },
          {
            title: '账号',
            dataIndex: 'username',
            width: 140,
            ellipsis: true,
          },
          {
            title: '昵称',
            width: 140,
            ellipsis: true,
            render: (_: unknown, row) => row.nickname ?? '—',
          },
          {
            title: '等级',
            dataIndex: 'level_code',
            width: 72,
          },
          {
            title: 'L',
            width: 88,
            align: 'right' as const,
            render: (_: unknown, row) => fmtSum(row.left_sum),
          },
          {
            title: 'R',
            width: 88,
            align: 'right' as const,
            render: (_: unknown, row) => fmtSum(row.right_sum),
          },
        ]}
        locale={{
          emptyText: friendsQuery.isError ? '加载失败' : '暂无直推好友',
        }}
        scroll={{ x: 720 }}
      />
    </div>
  );
}

type ModalProps = {
  open: boolean;
  userId?: number;
  username?: string;
  playerNumber?: string | null;
  onClose: () => void;
  onOpenPlayer?: (playerNumber: string, userId: number) => void;
};

export function DirectFriendsModal({
  open,
  userId,
  username,
  playerNumber,
  onClose,
  onOpenPlayer,
}: ModalProps) {
  const titleParts = [
    username || playerNumber || (userId != null ? `#${userId}` : ''),
    playerNumber ? `(${playerNumber})` : '',
    '· 直推好友',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Modal open={open} title={titleParts} footer={null} onCancel={onClose} destroyOnClose width={860}>
      {open && userId != null ? (
        <DirectFriendsPanel userId={userId} enabled={open} onOpenPlayer={onOpenPlayer} />
      ) : null}
    </Modal>
  );
}
